import { type PlayerTransfer, RankingType } from '@on3/api';
import { On3IconLockPremium, On3IconTransferRating } from '@on3/icons';
import { useAuth } from '@on3/ui-lib/index';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { Link } from 'components/Link';
import { On3LogoAlt } from 'components/Svg/On3LogoAlt';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';

import styles from './IndustryComparisonTpPlayerItem.module.scss';

interface IndustryComparisonTpPlayerItemProps {
  person?: PlayerTransfer | null;
  className?: string;
  showRank?: boolean;
  selected?: boolean;
}
export const IndustryComparisonTpPlayerItem = ({
  person,
  className,
  showRank,
  selected,
}: IndustryComparisonTpPlayerItemProps) => {
  const {
    defaultAsset,
    slug,
    height,
    weight,
    name,
    industryComparison,
    positionAbbreviation,
    nilValue,
    nilStatus,
    athleteVerified,
    transferRating,
  } = person || {};
  const router = useRouter();

  const { transferType } = router.query;
  const { user } = useAuth();
  const isTop = transferType?.toString() === 'top';
  const consensus = industryComparison ? industryComparison[0] : null;
  const consensusRating = transferRating
    ? transferRating?.consensusRating?.toFixed(2)
    : null;
  const joinLink = `/rivals/subscribe/`;
  const utm_campaign = isTop
    ? 'industry_comparison_top'
    : 'industry_comparison_wire';
  const utmParams = new URLSearchParams({
    utm_source: 'transfer_portal',
    utm_medium: 'web',
    utm_campaign,
    utm_content: 'lock',
  });

  const utmLink = `${joinLink}?${utmParams}`;
  const formattedNilValue = nilValue ? (
    <span className={styles.nilValue}>
      {nilValue ? (
        formatCurrency(nilValue)
      ) : user?.has ? (
        '-'
      ) : (
        <Link href={utmLink}>
          <On3IconLockPremium />
        </Link>
      )}
    </span>
  ) : (
    <span className={styles.valuationUndisclosed}>-</span>
  );

  return (
    <div
      className={clsx(styles.playerItemContainer, className, {
        [styles.noJerseyOrRank]: !showRank,
      })}
    >
      {showRank && transferRating?.consensusNationalRank && (
        <Text className={styles.jerseyNumber} component="span">
          {transferRating?.consensusNationalRank}
        </Text>
      )}
      <On3Avatar
        altText={defaultAsset?.altText}
        className={clsx(styles.playerAvatar, {
          [styles.noJerseyOrRank]: !showRank,
        })}
        domain={defaultAsset?.domain}
        source={defaultAsset?.source}
      />

      <div className={styles.playerDetailsContainer}>
        <div className={styles.playerName}>
          <TextLink
            color="primary"
            href={`/rivals/${slug}/`}
            underline="none"
            variant="h5"
          >
            {name}
          </TextLink>
          {athleteVerified && <AthleteVerified />}
        </div>
        <Text className={styles.vitalsContainer}>
          <span>{positionAbbreviation ?? '-'}</span>
          <span className={styles.separator} role="separator">
            /
          </span>
          <span>{height ?? '-'}</span>
          <span className={styles.separator} role="separator">
            /
          </span>
          <span>{weight ? weight : '-'}</span>
          <>
            <span className={styles.separator} role="separator">
              /
            </span>
            <span className={styles.nil}>
              <span className={styles.nilLabel}>NIL Value:</span>
              {formattedNilValue}
            </span>
          </>
        </Text>
      </div>

      {consensusRating && (
        <div className={clsx(styles.consensusRatingWrapper, styles.mobile)}>
          <Text className={styles.consensusRating} component="span">
            {consensusRating}
          </Text>

          {consensus?.rankingType === RankingType.TransferPortal ? (
            <On3IconTransferRating />
          ) : null}
        </div>
      )}

      <div className={clsx(styles.rankingHeaderContainer, styles.mobileHeader)}>
        <Text
          aria-label="National Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          Natl
        </Text>
        <Text
          aria-label="Position Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          Pos
        </Text>
        <Text
          aria-label="State Rank"
          className={styles.rankingHeader}
          component="span"
          variant="subtitle1"
        >
          St
        </Text>
      </div>

      <div
        className={clsx(styles.consensusStarRatingItem, {
          [styles.active]: selected,
          [styles.noJerseyOrRank]: !showRank,
        })}
      >
        <div className={styles.consensusLogoContainer}>
          <div
            className={clsx(styles.mobileServiceLogo, {
              [styles.active]: selected,
            })}
          >
            <On3LogoAlt className={styles.logo} fill="#a2a9ad" />
            <span>Industry</span>
          </div>
          <StarRating
            href={`/rivals/${slug}/industry-comparison/`}
            small
            sport={consensus?.sport}
            stars={transferRating?.consensusStars}
            year={`${consensus?.classYear}`}
          />
          {consensusRating && (
            <div
              className={clsx(styles.consensusRatingWrapper, styles.desktop)}
            >
              <Text className={styles.consensusRating} component="span">
                {consensusRating}
              </Text>
              {consensus?.rankingType === RankingType.TransferPortal ? (
                <On3IconTransferRating />
              ) : null}
            </div>
          )}
        </div>

        <div className={clsx(styles.rankingHeaderContainer)}>
          <Text
            aria-label="Industry National Rank"
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Natl
          </Text>
          <Text
            aria-label="Industry Position Rank"
            className={styles.rankingHeader}
            component="span"
            variant="subtitle1"
          >
            Pos
          </Text>
        </div>

        <div className={styles.consensusRankingsContainer}>
          <Text className={styles.rank} variant="subtitle1">
            {transferRating?.consensusNationalRank ?? '-'}
          </Text>
          <Text className={styles.rank} variant="subtitle1">
            {transferRating?.consensusPositionRank ?? '-'}
          </Text>
        </div>
      </div>
    </div>
  );
};
